import React, { createContext, useState, useEffect, useRef, useContext, useMemo, useCallback } from "react";
import { UA, WebSocketInterface } from "jssip";
import { AuthContext } from "../Auth/AuthContext";
import api from "../../services/api";
import { toast } from "react-toastify";
import alertSound from "../../assets/sound.mp3";

export const GlobalSoftphoneContext = createContext();

export const GlobalSoftphoneProvider = ({ children }) => {
  const { user, socket } = useContext(AuthContext);
  const [clientVoipConfig, setClientVoipConfig] = useState({});
  const [registered, setRegistered] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [callStatus, setCallStatus] = useState("desconectado");
  const [inCall, setInCall] = useState(false);
  const [incoming, setIncoming] = useState(false);
  const [incomingFrom, setIncomingFrom] = useState("");
  const [muted, setMuted] = useState(false);
  const [held, setHeld] = useState(false);
  const [activeNumber, setActiveNumber] = useState("");
  const [callDuration, setCallDuration] = useState(0);
  const [softphoneOpen, setSoftphoneOpen] = useState(false);
  const [voipLogs, setVoipLogs] = useState([]);

  const addVoipLog = useCallback((text, type = "info") => {
    const time = new Date().toLocaleTimeString();
    setVoipLogs((prev) => [
      { id: Date.now() + Math.random(), time, text, type },
      ...prev.slice(0, 99) // Manter até 100 registros de logs mais recentes
    ]);
  }, []);

  const clearVoipLogs = useCallback(() => {
    setVoipLogs([]);
  }, []);

  const openSoftphone = () => setSoftphoneOpen(true);
  const closeSoftphone = () => setSoftphoneOpen(false);
  const toggleSoftphone = () => setSoftphoneOpen((prev) => !prev);

  const uaRef = useRef(null);
  const sessionRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const localAudioStreamRef = useRef(null);
  const timerRef = useRef(null);
  const ringAudioRef = useRef(null);

  const startRingtone = useCallback(() => {
    try {
      if (!ringAudioRef.current) {
        ringAudioRef.current = new Audio(alertSound);
        ringAudioRef.current.loop = true;
      }
      ringAudioRef.current.currentTime = 0;
      ringAudioRef.current.play().catch((err) => {
        console.warn("[VOIP] Som de campainha bloqueado pelo navegador:", err);
      });
    } catch (_) {}
  }, []);

  const stopRingtone = useCallback(() => {
    try {
      if (ringAudioRef.current) {
        ringAudioRef.current.pause();
        ringAudioRef.current.currentTime = 0;
      }
    } catch (_) {}
  }, []);
  const connectionLostTimerRef = useRef(null);
  const hangupFallbackTimerRef = useRef(null);

  // Carregar configurações de VoIP do painel
  useEffect(() => {
    let mounted = true;
    const loadVoipConfig = async () => {
      try {
        const { data } = await api.get("/voip-settings/client");
        if (mounted && data && typeof data === "object") {
          setClientVoipConfig(data);
        }
      } catch (_) {
        if (mounted) setClientVoipConfig({});
      }
    };
    if (user?.id) {
      loadVoipConfig();
    }
    return () => {
      mounted = false;
    };
  }, [user]);

  const sipConfig = useMemo(() => {
    const runtimeEnv = (window && window.ENV) || {};
    let rawWsUrl =
      runtimeEnv.REACT_APP_SIP_WS_URL ||
      runtimeEnv.SIP_WS_URL ||
      clientVoipConfig.wsUrl ||
      "";
    let domain =
      runtimeEnv.REACT_APP_SIP_DOMAIN ||
      runtimeEnv.SIP_DOMAIN ||
      clientVoipConfig.sipDomain ||
      "";

    if (!domain && rawWsUrl) {
      try {
        const temp = rawWsUrl.includes("://") ? rawWsUrl : `ws://${rawWsUrl}`;
        domain = new URL(temp).hostname;
      } catch (_) {
        domain = rawWsUrl;
      }
    }

    let wsUrl = rawWsUrl.trim();
    if (wsUrl && !/^wss?:\/\//i.test(wsUrl)) {
      const isHttps = typeof window !== "undefined" && window.location.protocol === "https:";
      const scheme = isHttps ? "wss://" : "ws://";
      const defaultPort = isHttps ? "8089" : "8088";
      if (!wsUrl.includes(":")) {
        wsUrl = `${scheme}${wsUrl}:${defaultPort}/ws`;
      } else {
        wsUrl = `${scheme}${wsUrl}`;
      }
    }

    return { wsUrl, domain };
  }, [clientVoipConfig]);

  const sipExtension = String(clientVoipConfig.voip || user?.voip || "").trim();
  const sipAuthUser = String(clientVoipConfig.voipUser || user?.voipUser || sipExtension).trim();
  const sipPassword = String(clientVoipConfig.voipPass || "").trim();
  const isHttpsPage = typeof window !== "undefined" && window.location.protocol === "https:";
  const isMixedContentWs = isHttpsPage && /^ws:\/\//i.test(sipConfig.wsUrl || "");

  const canUseSoftphone = Boolean(
    sipConfig.wsUrl && sipConfig.domain && sipExtension && sipPassword && !isMixedContentWs
  );

  const getWebrtcPortLabel = useCallback(() => {
    const lowerWsUrl = String(sipConfig.wsUrl || "").toLowerCase();
    if (lowerWsUrl.startsWith("ws://") || lowerWsUrl.includes(":8088")) {
      return "Porta 8088";
    }
    return "Porta 8089";
  }, [sipConfig.wsUrl]);

  const shouldFallbackToAmi = useCallback((cause) => {
    const normalized = String(cause || "").toLowerCase();
    if (!normalized) return false;

    return /webrtc|connection|transport|peerconnection|ice|sdp|websocket|rtc/.test(
      normalized
    );
  }, []);

  useEffect(() => {
    if (!isMixedContentWs) return;

    setRegistered(false);
    setRegistering(false);
    setCallStatus("bloqueado: ws:// em pagina https");
    addVoipLog(
      "WebRTC bloqueado pelo navegador: a tela esta em HTTPS, mas o SIP usa ws://. Troque para wss://...:8089/ws ou abra o sistema em HTTP para teste local.",
      "error"
    );
  }, [addVoipLog, isMixedContentWs]);

  const rtcOptions = useCallback((extraOptions = {}) => ({
    mediaConstraints: { audio: true, video: false },
    rtcOfferConstraints: {
      offerToReceiveAudio: true,
      offerToReceiveVideo: false
    },
    rtcAnswerConstraints: {
      offerToReceiveAudio: true,
      offerToReceiveVideo: false
    },
    pcConfig: {
      // No ambiente local, os candidatos ICE devem permanecer na rede do host.
      // O endereço público será fornecido pelo Asterisk quando a instalação
      // estiver configurada para produção.
      iceServers: [],
      iceTransportPolicy: "all"
    },
    ...extraOptions
  }), []);

  const ensureLocalAudioStream = useCallback(async () => {
    if (localAudioStreamRef.current) {
      const hasLiveTrack = localAudioStreamRef.current
        .getAudioTracks()
        .some((track) => track.readyState === "live");

      if (hasLiveTrack) {
        return localAudioStreamRef.current;
      }
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("O navegador não suporta captura de microfone.");
    }

    addVoipLog("Solicitando acesso ao microfone do navegador...", "info");
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    localAudioStreamRef.current = stream;
    addVoipLog("Microfone liberado com sucesso para o softphone.", "success");
    return stream;
  }, [addVoipLog]);

  const playRemoteAudio = useCallback((peerconnection, event = null) => {
    if (!remoteAudioRef.current || !peerconnection) return;

    const eventStream = event?.streams?.[0];
    const remoteTracks = eventStream
      ? eventStream.getAudioTracks()
      : peerconnection
          .getReceivers()
          .map((receiver) => receiver.track)
          .filter((track) => track?.kind === "audio");

    if (!eventStream && remoteTracks.length === 0) return;

    const stream = eventStream || new MediaStream(remoteTracks);
    remoteAudioRef.current.srcObject = stream;
    remoteAudioRef.current.muted = false;
    remoteAudioRef.current.volume = 1;
    remoteAudioRef.current
      .play()
      .then(() => {
        addVoipLog("Fluxo de audio remoto reproduzindo no navegador.", "success");
      })
      .catch((error) => {
        addVoipLog(
          `O navegador bloqueou a reprodução do áudio remoto: ${error?.message || error}`,
          "error"
        );
      });
  }, [addVoipLog]);

  const makeAmiCall = useCallback(async (destNumber, ticketId = null, contactId = null) => {
    if (!destNumber) {
      toast.error("Informe o número para ligar");
      return;
    }
    if (!sipExtension) {
      toast.error("Seu usuário não possui um ramal VoIP configurado.");
      return;
    }
    setSoftphoneOpen(true);
    addVoipLog(`Disparando chamada via AMI para o número ${destNumber} (Ramal: ${sipExtension})...`, "info");
    try {
      await api.post("/voip/dial", {
        number: destNumber,
        ticketId,
        contactId
      });
      toast.success("Chamada disparada via Issabel AMI! Atenda seu telefone/ramal.");
      addVoipLog(`Chamada AMI disparada com sucesso para ${destNumber}. Atenda o ramal ${sipExtension}.`, "success");
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || "Erro ao disparar chamada AMI";
      toast.error(msg);
      addVoipLog(`Erro no disparo AMI: ${msg}`, "error");
    }
  }, [addVoipLog, sipExtension]);

  const cleanupSession = useCallback((nextStatus = null) => {
    stopRingtone();
    sessionRef.current = null;
    setInCall(false);
    setIncoming(false);
    setMuted(false);
    setHeld(false);
    setIncomingFrom("");
    setActiveNumber("");
    setCallDuration(0);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (connectionLostTimerRef.current) {
      clearTimeout(connectionLostTimerRef.current);
      connectionLostTimerRef.current = null;
    }
    if (hangupFallbackTimerRef.current) {
      clearTimeout(hangupFallbackTimerRef.current);
      hangupFallbackTimerRef.current = null;
    }
    if (remoteAudioRef.current) {
      remoteAudioRef.current.pause();
      remoteAudioRef.current.srcObject = null;
    }
    if (nextStatus) {
      setCallStatus(nextStatus);
    }
  }, [stopRingtone]);

  const makeSoftphoneCall = useCallback(async (destNumber, ticketId = null, contactId = null) => {
    if (!destNumber) return;
    setSoftphoneOpen(true);

    if (isMixedContentWs) {
      toast.error("WebRTC bloqueado: a pagina esta em HTTPS e o SIP esta em ws://. Use wss:// na porta 8089.");
      return;
    }

    if (!uaRef.current || !registered) {
      toast.error("Softphone WebRTC desconectado. Verifique as configurações de VoIP.");
      return;
    }

    if (sessionRef.current) {
      toast.error("Já existe uma chamada ativa.");
      return;
    }

    try {
      setCallStatus("discando...");
      setActiveNumber(destNumber);

      const target = `sip:${String(destNumber).replace(/\s/g, "")}@${sipConfig.domain}`;
      const localStream = await ensureLocalAudioStream();
      const session = uaRef.current.call(target, rtcOptions({ mediaStream: localStream }));
      session._wtDestination = String(destNumber).trim();
      session._wtTicketId = ticketId;
      session._wtContactId = contactId;
      sessionRef.current = session;
    } catch (error) {
      toast.error(error?.message || "Erro ao iniciar chamada Softphone");
      cleanupSession();
    }
  }, [cleanupSession, ensureLocalAudioStream, isMixedContentWs, registered, rtcOptions, sipConfig.domain]);

  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setCallDuration(0);
    timerRef.current = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);
  };

  // Conexão JsSIP constante em qualquer tela
  useEffect(() => {
    if (!canUseSoftphone) return undefined;

    try {
      const socketWs = new WebSocketInterface(sipConfig.wsUrl);
      const handleSocketMessage = socketWs._onMessage.bind(socketWs);
      socketWs._onMessage = (event) => {
        const rawMessage =
          typeof event?.data === "string"
            ? event.data
            : "";

        if (/^INVITE\s+/i.test(rawMessage)) {
          addVoipLog(
            "INVITE de entrada recebido pelo WebSocket do navegador.",
            "success"
          );
        }

        try {
          handleSocketMessage(event);
        } catch (error) {
          addVoipLog(
            `Falha ao processar mensagem SIP recebida: ${error?.message || error}`,
            "error"
          );
          throw error;
        }
      };
      const ua = new UA({
        sockets: [socketWs],
        uri: `sip:${sipExtension}@${sipConfig.domain}`,
        authorization_user: sipAuthUser,
        password: sipPassword,
        display_name: user?.name || sipExtension || "Agente",
        register: true,
        session_timers: false,
        register_expires: 120,
        connection_recovery_min_interval: 2,
        connection_recovery_max_interval: 30
      });

      uaRef.current = ua;
      setRegistering(true);
      setCallStatus("conectando");
      addVoipLog(`Tentando conexão WebSocket em: ${sipConfig.wsUrl}`, "info");

      let failAttempts = 0;

      ua.on("connecting", () => {
        setCallStatus("conectando");
        if (failAttempts === 0) {
          addVoipLog(`Conectando socket WebRTC...`, "info");
        }
      });
      ua.on("connected", () => {
        failAttempts = 0;
        setCallStatus("socket conectado");
        addVoipLog(`Socket WebRTC conectado com sucesso. Autenticando ramal ${sipExtension}...`, "success");
      });
      ua.on("registered", () => {
        failAttempts = 0;
        setRegistered(true);
        setRegistering(false);
        setCallStatus("online");
        addVoipLog(`Ramal ${sipExtension} registrado ONLINE via WebRTC no Issabel.`, "success");
      });
      ua.on("unregistered", () => {
        setRegistered(false);
        setCallStatus("offline");
        addVoipLog(`Ramal ${sipExtension} desregistrado do WebRTC.`, "warn");
      });
      ua.on("registrationFailed", (data) => {
        setRegistering(false);
        setRegistered(false);
        const cause = data?.cause || "Senha incorreta ou erro no registro";
        setCallStatus(`falha no registro: ${cause}`);
        addVoipLog(`Falha no registro SIP do ramal ${sipExtension}: ${cause}`, "error");
      });
      ua.on("disconnected", (data) => {
        failAttempts += 1;
        setRegistering(false);
        setRegistered(false);
        const cause = data?.cause || `${getWebrtcPortLabel()} inativa (Connection Refused)`;
        setCallStatus(`desconectado (WebRTC inativo: ${cause})`);
        if (sessionRef.current) {
          addVoipLog("Chamada finalizada porque o WebSocket SIP foi desconectado.", "warn");
          cleanupSession(`desconectado: ${cause}`);
        }

        if (failAttempts === 1) {
          addVoipLog(`WebSocket Desconectado (${cause}). As chamadas funcionarão via AMI.`, "error");
        }

        if (failAttempts >= 2) {
          addVoipLog(
            `WebRTC ainda indisponível em ${getWebrtcPortLabel()}. A reconexão automática continuará ativa.`,
            "warn"
          );
        }
      });

      ua.on("newRTCSession", (data) => {
        const session = data.session;
        const isIncoming = data.originator === "remote";

        if (sessionRef.current && sessionRef.current !== session) {
          session.terminate({ status_code: 486, reason_phrase: "Busy Here" });
          return;
        }

        sessionRef.current = session;
        const callerNum =
          session.remote_identity?.uri?.user || session.remote_identity?.display_name || "Desconhecido";
        const sessionNumber = session._wtDestination || callerNum;

        setIncomingFrom(callerNum);
        setActiveNumber(sessionNumber);
        setIncoming(isIncoming);
        setInCall(!isIncoming);
        setCallStatus(isIncoming ? "chamada recebida" : "chamando...");
        setSoftphoneOpen(true);

        if (isIncoming) {
          startRingtone();
        }

        session.on("connecting", () => {
          addVoipLog(
            isIncoming
              ? `Preparando atendimento SIP de ${sessionNumber}...`
              : `Preparando chamada SIP para ${sessionNumber}...`,
            "info"
          );
        });

        session.on("accepted", () => {
          stopRingtone();
          setInCall(true);
          setIncoming(false);
          setCallStatus("em chamada");
          startTimer();
          setTimeout(() => playRemoteAudio(session.connection), 0);
          addVoipLog(`Chamada aceita com ${sessionNumber}. Negociando audio WebRTC...`, "success");
        });

        session.on("confirmed", () => {
          setInCall(true);
          setIncoming(false);
          setCallStatus("em chamada");
          setTimeout(() => playRemoteAudio(session.connection), 0);
          addVoipLog(`Chamada confirmada por SIP com ${sessionNumber}.`, "success");
        });

        session.on("ended", (event) => {
          const cause = event?.cause || "Encerrada";
          addVoipLog(`Chamada encerrada (${cause}).`, "warn");
          cleanupSession(uaRef.current?.isRegistered?.() ? "online" : "offline");
        });

        session.on("failed", (event) => {
          const cause = event?.cause || "SIP Failure Code";
          const fallbackNumber = session._wtDestination || sessionNumber;
          addVoipLog(`Chamada WebRTC encerrada/falhou (${cause}).`, "warn");
          cleanupSession(`falhou: ${cause}`);
          if (!isIncoming && sipExtension && fallbackNumber && shouldFallbackToAmi(cause)) {
            addVoipLog(`Alternando para discagem via AMI...`, "info");
            makeAmiCall(fallbackNumber);
          }
        });

        session.on("getusermediafailed", (error) => {
          setCallStatus("falhou: microfone bloqueado");
          addVoipLog(`Falha ao abrir microfone: ${error?.message || error}`, "error");
        });

        session.on("peerconnection:createanswerfailed", (error) => {
          setCallStatus("falhou: erro ao criar resposta WebRTC");
          addVoipLog(`Erro ao criar SDP de resposta: ${error?.message || error}`, "error");
        });

        session.on("peerconnection:createofferfailed", (error) => {
          setCallStatus("falhou: erro ao criar oferta WebRTC");
          addVoipLog(`Erro ao criar SDP de oferta: ${error?.message || error}`, "error");
        });

        session.on("peerconnection:setlocaldescriptionfailed", (error) => {
          setCallStatus("falhou: erro ao publicar SDP local");
          addVoipLog(`Erro ao aplicar SDP local: ${error?.message || error}`, "error");
        });

        session.on("peerconnection:setremotedescriptionfailed", (error) => {
          setCallStatus("falhou: erro ao aplicar SDP remoto");
          addVoipLog(`Erro ao aplicar SDP remoto: ${error?.message || error}`, "error");
        });

        session.on("peerconnection", ({ peerconnection }) => {
          peerconnection.oniceconnectionstatechange = () => {
            const iceState = peerconnection.iceConnectionState || "desconhecido";
            addVoipLog(`ICE state: ${iceState}`, iceState === "failed" ? "error" : "info");

            if (iceState === "failed" || iceState === "closed") {
              cleanupSession(uaRef.current?.isRegistered?.() ? "online" : "offline");
            }
          };

          peerconnection.onconnectionstatechange = () => {
            const connectionState = peerconnection.connectionState || "desconhecido";
            addVoipLog(
              `PeerConnection state: ${connectionState}`,
              connectionState === "failed" ? "error" : "info"
            );

            if (connectionState === "connected" && connectionLostTimerRef.current) {
              clearTimeout(connectionLostTimerRef.current);
              connectionLostTimerRef.current = null;
            } else if (connectionState === "disconnected") {
              if (connectionLostTimerRef.current) {
                clearTimeout(connectionLostTimerRef.current);
              }
              connectionLostTimerRef.current = setTimeout(() => {
                if (peerconnection.connectionState === "disconnected") {
                  addVoipLog("Chamada encerrada após perda da conexão de mídia.", "warn");
                  cleanupSession(uaRef.current?.isRegistered?.() ? "online" : "offline");
                }
              }, 5000);
            } else if (connectionState === "failed" || connectionState === "closed") {
              cleanupSession(uaRef.current?.isRegistered?.() ? "online" : "offline");
            }
          };

          peerconnection.ontrack = (event) => {
            playRemoteAudio(peerconnection, event);
          };
        });
      });

      ua.start();
    } catch (err) {
      console.error("[VOIP] Erro ao iniciar JsSIP:", err);
    }

    return () => {
      try {
        if (sessionRef.current) sessionRef.current.terminate();
      } catch (_) {}
      cleanupSession();
      setRegistered(false);
      setRegistering(false);
      if (localAudioStreamRef.current) {
        localAudioStreamRef.current.getTracks().forEach((track) => track.stop());
        localAudioStreamRef.current = null;
      }
      if (uaRef.current) uaRef.current.stop();
      uaRef.current = null;
    };
  }, [
    addVoipLog,
    canUseSoftphone,
    cleanupSession,
    ensureLocalAudioStream,
    getWebrtcPortLabel,
    makeAmiCall,
    playRemoteAudio,
    shouldFallbackToAmi,
    sipAuthUser,
    sipConfig,
    sipExtension,
    sipPassword,
    startRingtone,
    stopRingtone,
    user
  ]);

  // Ouvir Socket.IO para sincronização real-time de chamadas do backend
  useEffect(() => {
    if (!socket || !user?.companyId) return;

    const onVoipCall = (data) => {
      if (data?.action === "create" || data?.action === "update") {
        const call = data?.voipCall;
        if (!call) return;
        const status = call?.status || "desconhecido";
        addVoipLog(`Evento AMI: chamada ${call.id || "-"} atualizada para ${status}.`, "info");
      }
    };

    socket.on(`company-${user.companyId}-voipCall`, onVoipCall);

    return () => {
      socket.off(`company-${user.companyId}-voipCall`, onVoipCall);
    };
  }, [addVoipLog, socket, user]);

  const answerCall = async () => {
    if (!sessionRef.current) return;
    try {
      const localStream = await ensureLocalAudioStream();
      addVoipLog(`Atendendo chamada recebida de ${incomingFrom || "contato"}...`, "info");
      setCallStatus("atendendo...");
      sessionRef.current.answer(rtcOptions({ mediaStream: localStream }));
    } catch (error) {
      toast.error(error?.message || "Erro ao atender chamada");
      setCallStatus(`falhou ao atender: ${error?.message || "erro desconhecido"}`);
      addVoipLog(`Erro ao atender chamada: ${error?.message || error}`, "error");
    }
  };

  const rejectCall = () => {
    if (!sessionRef.current) return;
    sessionRef.current.terminate({ status_code: 486, reason_phrase: "Busy Here" });
    cleanupSession();
  };

  const hangupCall = () => {
    if (!sessionRef.current) return;
    const session = sessionRef.current;
    setCallStatus("encerrando...");
    addVoipLog("Enviando encerramento SIP para a chamada...", "info");
    try {
      session.terminate();
      hangupFallbackTimerRef.current = setTimeout(() => {
        if (sessionRef.current === session) {
          cleanupSession(uaRef.current?.isRegistered?.() ? "online" : "offline");
        }
      }, 2000);
    } catch (error) {
      addVoipLog(
        `Falha ao enviar encerramento SIP: ${error?.message || error}`,
        "error"
      );
      cleanupSession();
    }
  };

  const toggleMute = () => {
    if (!sessionRef.current) return;
    if (muted) {
      sessionRef.current.unmute({ audio: true });
      setMuted(false);
    } else {
      sessionRef.current.mute({ audio: true });
      setMuted(true);
    }
  };

  const sendDTMF = (digit) => {
    if (!sessionRef.current || !inCall) return;
    sessionRef.current.sendDTMF(String(digit));
    toast.info(`Tom DTMF enviado: ${digit}`);
  };

  return (
    <GlobalSoftphoneContext.Provider
      value={{
        softphoneOpen,
        setSoftphoneOpen,
        openSoftphone,
        closeSoftphone,
        toggleSoftphone,
        voipLogs,
        addVoipLog,
        clearVoipLogs,
        registered,
        registering,
        callStatus,
        inCall,
        incoming,
        incomingFrom,
        muted,
        held,
        activeNumber,
        callDuration,
        canUseSoftphone,
        isMixedContentWs,
        userVoip: sipExtension,
        makeSoftphoneCall,
        makeAmiCall,
        answerCall,
        rejectCall,
        hangupCall,
        toggleMute,
        sendDTMF
      }}
    >
      {children}
      <audio ref={remoteAudioRef} autoPlay playsInline />
    </GlobalSoftphoneContext.Provider>
  );
};
